import { FormikProps } from "formik";
import {
  OrderInterface,
  ProductOrderInterface,
} from "../../../interfaces/orderInterface";
import SelectFilter from "../../../components/common/Inputs/SelectFilter";
import { useCallback, useMemo, useState } from "react";
import { useProductsFilter, useProduct } from "../../../features/products";
import { useDebounce } from "../../../utils/useDebounce";
import CustomInput from "../../../components/common/Inputs/CustomInput";
import Button from "../../../components/common/Buttons/Button";
import { FaPlus, FaRegTimesCircle, FaSave } from "react-icons/fa";
import ProductsTable from "./ProductsTable";
import { DealerInterface } from "../../../interfaces/dealerInterface";
import { ShippingCost } from "../../../interfaces/shippingCostType";
import { useProductsOrderStore } from "../../../stores/useProductsOrderStore";

type ProductsFormProps = {
  formik: FormikProps<OrderInterface>; // Define the formik prop
  selectedDealer?: DealerInterface;
  isTaxRegimeMoral?: boolean;
};

const ProductsForm = ({
  formik,
  selectedDealer,
  isTaxRegimeMoral,
}: ProductsFormProps) => {
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const debouncedProductSearchTerm = useDebounce(productSearchTerm, 400);
  const { data: products } = useProductsFilter({
    filter: { search: debouncedProductSearchTerm },
    pagination: { page: 1, pageSize: 20, sortBy: "name", sortOrder: "asc" },
  });

  const [selectedProductId, setSelectedProductId] = useState<string>();
  const { data: selectedProduct } = useProduct({ productId: selectedProductId });
  const [quantity, setQuantity] = useState<number | undefined>();
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // We use the zustand store to manage selected products across the order form
  const addSelectedProduct = useProductsOrderStore((s) => s.addProduct);
  const removeSelectedProduct = useProductsOrderStore((s) => s.removeProduct);
  const updateSelectedProduct = useProductsOrderStore((s) => s.updateQuantity);

  const shippingCost: ShippingCost | undefined = useMemo(() => {
    return formik.values.shippingCost;
  }, [formik.values.shippingCost]);

  const handleProductOrderOnChange = (value: string) => {
    setSelectedProductId(value);
  };

  const handleAddProduct = useCallback(() => {
    if (selectedProduct && quantity && selectedDealer?._id) {
      if (isEditing) {
        updateSelectedProduct(selectedProduct._id!, quantity);
        clearForm();
        return;
      }
      addSelectedProduct(selectedProduct._id!, quantity);
      clearForm();
    }
  }, [selectedProduct, quantity, selectedDealer?._id]);

  const handleEdit = (product: ProductOrderInterface) => {
    setSelectedProductId(product._id);
    setQuantity(product.quantity);
    setIsEditing(true);
  };

  const handleDelete = (product: ProductOrderInterface) => {
    removeSelectedProduct(product._id);
  };

  const handleQuantityOnChange = (value: string) => {
    setQuantity(selectedProduct?.isBulk ? parseFloat(value) : parseInt(value));
  };

  const clearForm = () => {
    setSelectedProductId(undefined);
    setQuantity(undefined);
    setIsEditing(false);
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectFilter
          label="Producto"
          data={products || []}
          dataKey={"_id"}
          dataLabel={"name"}
          selected={selectedProductId ?? ""}
          selectedValue={selectedProduct}
          onSelect={handleProductOrderOnChange}
          onSearchTermChange={setProductSearchTerm}
          error={
            formik.touched.products &&
            formik.errors.products &&
            Array.isArray(formik.errors.products)
              ? formik.errors.products.join(", ")
              : formik.errors.products
          }
        />
        <div className="flex justify-between md:grid md:grid-cols-2 gap-4">
          <div className="col-span-1">
            <CustomInput
              label="Cantidad"
              type="number"
              onChange={(e) => handleQuantityOnChange(e.target.value)}
              value={quantity ? quantity?.toString() : ""}
              disabled={!selectedProduct}
              min={1}
            />
          </div>
          <div className="col-span-1 mt-4">
            <Button
              size="medium"
              color="primary"
              className="inline-block mt-2 h-11"
              onClick={handleAddProduct}
            >
              {isEditing ? <FaSave /> : <FaPlus />}
            </Button>
            {isEditing && (
              <Button
                size="medium"
                color="secondary"
                className="inline-block mt-2 ml-2 h-11"
                onClick={clearForm}
              >
                <FaRegTimesCircle />
              </Button>
            )}
          </div>
        </div>
      </div>
      <ProductsTable
        products={formik.values.products}
        onEdit={handleEdit}
        onDelete={handleDelete}
        shippingCost={shippingCost}
        applyShippingCost={formik.values.applyShippingCost}
        isTaxRegimeMoral={isTaxRegimeMoral}
      />
    </div>
  );
};

export default ProductsForm;
